import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { Plus } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { costApplicationStatusVariant, costContractStatusVariant } from '@/lib/status-variants'
import * as Query from '../../../api/gql/query'
import { useRouter } from '../../../hooks'
import ApplicationDialog from '../applications/ApplicationDialog'
import { useApplicationMutations } from '../hooks/useApplicationMutations'
import { useCostCurrency } from '../hooks/useCostCurrency'
import { newApplicationBlocker } from '../lib/applications'
import { CONTRACT_STATUS_KEY } from '../lib/contracts'
import { describeCostControlError } from '../lib/errors'
import { APPLICATION_STATUS_KEY } from '../lib/labels'
import { formatMoney, moneyLocale } from '../lib/money'
import type { CostContract } from '../types'
import MyContractsSkeleton from './MyContractsSkeleton'

interface Props { projectId: string }

/** The module page of a contractor's representative (1b-B spec §8): his contracts only, with "Nový soupis" where allowed. */
const MyContracts: React.FC<Props> = ({ projectId }) => {
  const { t, i18n } = useTranslation()
  const locale = moneyLocale(i18n.language)
  const { history } = useRouter()
  const currency = useCostCurrency(projectId)
  const { data, loading, error } = useQuery<{ costContracts: CostContract[] }>(Query.COST_CONTRACTS, {
    variables: { projectId, filter: {} },
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  const [creatingFor, setCreatingFor] = useState<string | null>(null)
  const mutations = useApplicationMutations(projectId, creatingFor ?? '')
  const base = `/project/${projectId}/cost-control`
  const money = (v: string) => formatMoney(v, { locale, currency })

  if (loading && !data) return <MyContractsSkeleton />
  if (error && !data) return <p className="text-sm text-muted-foreground">{describeCostControlError(error.message, t)}</p>
  const contracts = data?.costContracts ?? []

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold">{t('CostControl_my_contracts_title')}</h2>
      {contracts.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('CostControl_my_contracts_empty')}</p>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('CostControl_contract_field_name')}</TableHead>
                <TableHead className="w-28">{t('CostControl_contract_field_status')}</TableHead>
                <TableHead className="w-40 text-right">{t('CostControl_contract_current_value')}</TableHead>
                <TableHead className="w-40 text-right">{t('CostControl_contract_invoiced')}</TableHead>
                <TableHead className="w-36">{t('CostControl_my_contracts_last_application')}</TableHead>
                <TableHead className="w-36" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {contracts.map((c) => {
                const blocker = newApplicationBlocker(c, c.lastApplicationStatus ? [{ status: c.lastApplicationStatus }] : [])
                return (
                  <TableRow key={c.id} className="cursor-pointer" onClick={() => history.push(`${base}/contracts/${c.id}`)}>
                    <TableCell>
                      <div className="font-medium">{c.name}</div>
                      {c.number && <div className="text-xs text-muted-foreground">{c.number}</div>}
                    </TableCell>
                    <TableCell><Badge variant={costContractStatusVariant(c.status)}>{t(CONTRACT_STATUS_KEY[c.status])}</Badge></TableCell>
                    <TableCell className="text-right tabular-nums">{money(c.totals.currentValue)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(c.totals.invoiced)}</TableCell>
                    <TableCell>
                      {c.lastApplicationStatus
                        ? <Badge variant={costApplicationStatusVariant(c.lastApplicationStatus)}>{t(APPLICATION_STATUS_KEY[c.lastApplicationStatus])}</Badge>
                        : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!!blocker}
                        title={blocker ? t(blocker) : undefined}
                        onClick={(e) => { e.stopPropagation(); setCreatingFor(c.id) }}
                      >
                        <Plus className="h-4 w-4" />
                        {t('CostControl_application_new')}
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}
      <ApplicationDialog
        open={!!creatingFor}
        mode="create"
        onOpenChange={(open) => { if (!open) setCreatingFor(null) }}
        onSubmit={async (input) => {
          const res = await mutations.create(input)
          if (res.ok) history.push(`${base}/applications/${res.data.createCostApplication.id}`)
          return res.ok
        }}
      />
    </div>
  )
}
export default MyContracts
